import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, TextField, Select, MenuItem,
  FormControl, Button, Box, Stack, Typography, Divider, Autocomplete,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel
} from "@mui/material";
import Loader from "src/utils/Loader";
import DataTable, { TableColumn } from "react-data-table-component";
import { KTIcon } from "src/marketConnect/helpers";
import { getAllPlatformTypesAsync } from "src/redux/actions/platformActions";
import { useAppDispatch, useAppSelector } from "src/redux/storeHooks";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";
import { getAllProductsVarientsFromChannelAsync } from "src/redux/actions/productActions";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { createBundleItemAsync, getEditBundleDataAsync, updateBundleItemAsync } from "src/redux/actions/activeBundleAction";
import { usePermission } from "src/hooks/usePermission";

export type BundleComponent = {
  id: string;
  platformId?: string;
  platformName: string | "";
  productId?: string | "";
  productLabel: string;
  quantity: number;
  isDeleted?: boolean;
  unitPrice: number | null;
  sku?: string;
};

export type BundleData = {
  id?: Number;
  name?: string;
  parentProductId?: string;
  parentProductName?: string;
  productLabel?: string;
  components: BundleComponent[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  initialBundle?: BundleData | null;
  onSave?: (bundle: BundleData, closeAfterSave?: boolean) => void;
  currentPlatformId?: number | string | null;
};

const genId = (prefix = "") => `${prefix}${Math.random().toString(36).slice(2, 9)}`;

export default function BundleEditorModal({ open, onClose, initialBundle, currentPlatformId, onSave }: Props) {
  const dispatch = useAppDispatch();
  const { isRead, isWrite, isDelete } = usePermission();
  const [name, setName] = useState(initialBundle?.name ?? "");
  const [parentProductId, setParentProductId] = useState<string | "">(initialBundle?.parentProductId ?? "");
  const [components, setComponents] = useState<BundleComponent[]>(initialBundle?.components ?? []);
  const [platforms, setPlatforms] = useState<{ id: string; name: string }[]>([]);
  const [platformName, setPlatformName] = useState<string | "">("");
  const [productId, setProductId] = useState<string | "">("");
  const [quantity, setQuantity] = useState<number>(1);
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const [parentProductsOptions, setParentProductsOptions] = useState<{ id: string; name: string, subname: string }[]>([]);
  const [parentProductSearchText, setParentProductSearchText] = useState<string>("");
  const [parentPage, setParentPage] = useState(1);
  const [parentHasMore, setParentHasMore] = useState(true);
  const [parentLoading, setParentLoading] = useState(false);

  const [filteredProductsList, setFilteredProductsList] = useState<{ id: string; name: string, sku?: string }[]>([]);
  const [search, setSearch] = useState<string>(""); // input search for component product
  const [productPage, setProductPage] = useState(1);
  const [productHasMore, setProductHasMore] = useState(true);
  const [productLoading, setProductLoading] = useState(false);
  const { productTablePaginationData } = useAppSelector(state => state.productsOfSingleChannel);
  const [editLoading, setEditLoading] = useState(false);
  const [status, setStatus] = useState<boolean>(true);

  const resetAllFields = () => {
    setName("");
    setParentProductId("");
    setParentProductSearchText("");
    setComponents([]);
    setPlatformName("");
    setProductId("");
    setQuantity(1);
    setEditingId(null);
    setError(null);
    setSearch("");
    setParentProductsOptions([]);
    setFilteredProductsList([]);
    setParentPage(1);
    setProductPage(1);
    setUnitPrice(null);
  };

  const hasDestinationPlatform =
    currentPlatformId !== null &&
    currentPlatformId !== undefined &&
    `${currentPlatformId}`.trim() !== "" &&
    `${currentPlatformId}` !== "null" &&
    `${currentPlatformId}` !== "undefined";

  // Fetch Parent Products
  const fetchParentProducts = async (searchTerm = "", page = 1) => {
    if (!hasDestinationPlatform) return;
    setParentLoading(true);
    try {
      const paginationData = { ...productTablePaginationData, page, search_string: searchTerm };
      const res = await dispatch(
        getAllProductsVarientsFromChannelAsync({
          id: String(currentPlatformId), paginationdata: paginationData,
        })
      ).unwrap();
      const newData = res?.results?.map((p: any) => ({ id: p.id, name: p.platform_product?.name, subname: p?.name + (p?.sku !== null ? " - " + p.sku : "") })) || [];
      if (page === 1) setParentProductsOptions(newData);
      else setParentProductsOptions(prev => [...prev, ...newData]);

      setParentHasMore(res?.next !== null);
    } catch (err) {
      console.error(err);
    } finally {
      setParentLoading(false);
    }
  };

  // Fetch Component Products (dependent on platform)
  const fetchComponentProducts = async (platformId: string, searchTerm = "", page = 1) => {
    if (!platformId) return;
    setProductLoading(true);
    try {
      const paginationData = { ...(productTablePaginationData || {}), page, search_string: searchTerm };
      const res = await dispatch(
        getAllProductsVarientsFromChannelAsync({
          id: String(platformId),
          paginationdata: paginationData,
        })
      ).unwrap();
      const newData = res?.results?.map((p: any) => ({ id: p.id, name: p.platform_product?.name, sku: p?.name + (p?.sku !== null ? " - " + p.sku : "") })) || [];

      if (page === 1) setFilteredProductsList(newData);
      else setFilteredProductsList(prev => [...prev, ...newData]);

      setProductHasMore(res?.next !== null);
    } catch (err) {
      console.error(err);
    } finally {
      setProductLoading(false);
    }
  };

  // Effects for initial load
  useEffect(() => {
    if (open && hasDestinationPlatform) fetchParentProducts(parentProductSearchText, 1);
  }, [open, parentProductSearchText, currentPlatformId, hasDestinationPlatform]);

  useEffect(() => {
    if (platformName) fetchComponentProducts(platformName, search, 1);
  }, [platformName, search, open]);

  // Handle infinite scroll
  const handleParentScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 1 && parentHasMore && !parentLoading) {
      const nextPage = parentPage + 1;
      setParentPage(nextPage);
      fetchParentProducts(parentProductSearchText, nextPage);
    }
  };

  const handleProductScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 1 && productHasMore && !productLoading) {
      const nextPage = productPage + 1;
      setProductPage(nextPage);
      fetchComponentProducts(platformName, search, nextPage);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (initialBundle) {
      setEditLoading(true);
      // ----- EDIT MODE -----
      dispatch(getEditBundleDataAsync({ id: Number(initialBundle) }))
        .unwrap()
        .then((response: any) => {
          const res = response?.data;

          setName(res?.display_name || "");
          setParentProductId(res?.platform_product_variant?.id || "");
          setParentProductSearchText(res?.platform_product_variant?.name || "");
          const selectedPlatform = platforms.find(
            p => p.id === res.bundle_relation_data[0]?.platform_id
          )?.id || "";
          setStatus(res.is_active);
          setPlatformName(selectedPlatform);
          setComponents(
            res.bundle_relation_data?.map((c: any) => ({
              id: c.id,
              platformId: c.platform_id,
              platformName: platforms.find(p => p.id === c.platform_id)?.name || "",
              productId: c.platform_product_variant?.id,
              productLabel: c.platform_product_variant?.name || "",
              sku: c.platform_product_variant?.sku || "",
              quantity: c.quantity,
              unitPrice: c.unit_price ?? null,
              isDeleted: false
            })) || []
          );
        })
        .catch(err => {
          console.error("Failed to fetch bundle data:", err);
          setError("Failed to load bundle data.");
        })
        .finally(() => {
          setEditLoading(false);
        });
    } else {
      // ----- CREATE MODE -----
      resetAllFields();
    }
  }, [open, initialBundle, dispatch, platforms]);

  // Load platforms
  useEffect(() => {
    if (currentPlatformId != null) {
      dispatch(getAllPlatformTypesAsync({ platform_id: String(currentPlatformId) }))
        .unwrap()
        .then(res => {
          const filterList = res?.data?.map(item => ({
            id: item.id,
            name: `${item.name} [${item.type.charAt(0).toUpperCase() + item.type.slice(1)}]`
          }));
          setPlatforms(filterList);
        })
        .catch(err => console.error(err));
    }
  }, [currentPlatformId, dispatch]);

  const visibleComponents = components.filter(c => !c.isDeleted);
  const totalQuantity = useMemo(() => visibleComponents.reduce((sum, c) => sum + c.quantity, 0), [visibleComponents]);

  const distinctCount = useMemo(
    () => new Set(visibleComponents.map(c => c.productId)).size,
    [visibleComponents]
  );

  // Add/update component
  const handleAddOrUpdate = useCallback(() => {
    if (!platformName || !productId || quantity < 1) {
      setError("Please select platform, component, and valid quantity.");
      return;
    }

    if (visibleComponents.length > 0) {
      const existingPlatform = visibleComponents[0].platformId;
      if (existingPlatform && existingPlatform !== platformName) {
        setError("All components must belong to the same Destination. Remove current items to change platform.");
        return;
      }
    }

    const label = filteredProductsList.find(p => p.id === productId)?.name ?? "";
    const namePlatform = platforms.find(p => p.id === platformName)?.name ?? "";
    const selectedProduct = filteredProductsList.find(p => p.id === productId);
    const sku = selectedProduct?.sku ?? "";

    if (editingId) {
      setComponents(prev => prev.map(c =>
        c.id === editingId ? { ...c, platformId: platformName, platformName: namePlatform, productId, productLabel: label, sku, quantity, unitPrice: unitPrice, } : c
      ));
      setEditingId(null);
    } else {
      setComponents(prev => [
        ...prev,
        { id: genId("c_"), platformId: platformName, platformName: namePlatform, productId, productLabel: label, quantity, sku, isDeleted: false, unitPrice: unitPrice, }
      ]);
    }

    setProductId("");
    setQuantity(1);
    setError(null);
    setSearch("");
    setUnitPrice(null);

    // Reset product list for the current platform
  }, [platformName, productId, quantity, unitPrice, editingId, visibleComponents, filteredProductsList, platforms]);

  // Edit component
  const handleEdit = useCallback((index: number) => {
    const c = visibleComponents[index];
    if (!c) return;
    setPlatformName(c.platformId ?? "");
    setProductId(c.productId ?? "");
    setQuantity(c.quantity);
    setUnitPrice(c.unitPrice ?? null);
    setEditingId(c.id);
    setSearch(c?.productLabel)
  }, [visibleComponents]);

  // Delete component
  const handleDelete = useCallback((index: number) => {
    setComponents(prev => {
      const updated = prev.filter((_, i) => i !== index);
      return updated;
    });
  }, []);
  const confirmDelete = () => {
    if (deleteIndex !== null) {
      handleDelete(deleteIndex);
    }
    setShowDeleteModal(false);
    setDeleteIndex(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteIndex(null);
  };
  const handleDeleteClick = (index: number) => {
    setDeleteIndex(index);
    setShowDeleteModal(true);
  };

  const validate = useCallback(() => {
    if (!name.trim()) { setError("Bundle Name / Description is required."); return false; }
    if (!parentProductId) { setError("Parent Product SKU is required."); return false; }
    if (visibleComponents.length === 0) { setError("At least one active component is required."); return false; }
    setError(null);
    return true;
  }, [name, parentProductId, visibleComponents]);

  const handleSave = () => {
    if (!validate()) return;
    if (!hasDestinationPlatform) {
      setError("Destination marketplace is not selected. Close and choose a channel tab on the Kits & Bundles page.");
      return;
    }

    const bundle_relation_data = visibleComponents.map(c => ({
      platform_id: c.platformId,
      platform_product_variant_id: c.productId,
      quantity: c.quantity,
      unit_price: c.unitPrice ?? null,
    }));

    const isEdit = Boolean(initialBundle);

    const apiPayload: any = {
      platform_id: String(currentPlatformId),
      name: name,
      platform_product_variant_id: parentProductId,
      bundle_relation_data
    };

    if (isEdit) {
      apiPayload.bundle_relation_id = initialBundle;
      apiPayload.is_active = status;
    }

    const action = isEdit
      ? updateBundleItemAsync({ payloadupdate: apiPayload })
      : createBundleItemAsync(apiPayload);

    dispatch(action)
      .unwrap()
      .then((res) => {
        // ✔ Show API message if available; otherwise fallback
        const successMessage =
          res?.message ||
          (isEdit
            ? "Bundle relation updated successfully!"
            : "Bundle relation created successfully!"
          );

        // ✔ Use your toast
        toast?.success(successMessage);

        onSave?.(apiPayload, true);
        onClose();
      })
      .catch((err) => {
        console.error("Bundle Save Err", err?.data?.massage);
        toast?.error(err?.data?.massage || "Failed to save bundle.");
      });
  };


  const columns: TableColumn<BundleComponent>[] = useMemo(() => [
    { name: "Destination(Platform)", selector: (row) => row.platformName, sortable: true },
    {
      name: "Component Product / Name", cell: row => (
        <Box><Typography fontWeight={400}>{row.productLabel}</Typography></Box>
      ), grow: 2
    },
    {
      name: "SKU",
      cell: row => (
        <Box><Typography fontWeight={400}>{row.sku}</Typography></Box>
      ), grow: 2
    },
    { name: "Quantity", selector: row => row.quantity.toString(), sortable: true, width: "120px" },
    {
      name: "Unit Price",
      selector: (row) => (row.unitPrice == null ? "" : row.unitPrice.toString()),
      sortable: true,
      width: "120px",
    },
    {
      name: "Action", cell: (_, index) => (
        <Stack direction="row" spacing={1}>
          {isWrite && (<button className="btn btn-icon btn-bg-light btn-color-primary btn-sm me-3 mt-1" onClick={() => handleEdit(index)}>
            <KTIcon iconName="pencil" className="fs-4" />
          </button>)}
          {isDelete && (<button className="btn btn-icon btn-bg-light btn-color-danger btn-sm mt-1" onClick={() => handleDeleteClick(index)}>
            <KTIcon iconName="trash" className="fs-4" />
          </button>)}
        </Stack>
      ), width: "160px"
    },
  ], [handleEdit, handleDeleteClick]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" style={{ zIndex: "999" }}>
        <DialogTitle sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderBottom: "1px solid rgb(225, 225, 225)", mb: 2
        }}>
          <Typography variant="h6">Bundle Definition Editor</Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => { resetAllFields(); onClose(); }}
              sx={{ backgroundColor: "#2563eb", "&:hover": { backgroundColor: "#1e4bb8" } }}>Close</Button>
            {isWrite && (<Button variant="contained" onClick={handleSave} sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#13803d" } }}
            >Save Bundle</Button>)}
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ position: "relative" }}>

          {!hasDestinationPlatform && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Destination marketplace is missing. Close this dialog, select a channel tab at the top of Kits & Bundles, then try again.
            </Alert>
          )}

          {editLoading && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.6)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2000,
              }}
            >
              <Loader variant="section" />
            </div>
          )}
          {/* Define Bundle */}
          <Box sx={{ p: 2, mb: 2, borderRadius: 2, boxShadow: 1, backgroundColor: "#fff" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                1. Define the Bundle
              </Typography>
              {initialBundle && (
                <FormControl component="fieldset">
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <FormLabel
                      component="span"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 550,
                        color: "text.primary",
                      }}
                    >
                      Status:
                    </FormLabel>

                    <RadioGroup
                      row
                      value={status ? "active" : "inactive"}
                      onChange={(e) => setStatus(e.target.value === "active")}
                    >
                      <FormControlLabel
                        value="active"
                        control={<Radio size="small" />}
                        label="Active"
                      />
                      <FormControlLabel
                        value="inactive"
                        control={<Radio size="small" />}
                        label="Inactive"
                      />
                    </RadioGroup>
                  </Box>
                </FormControl>
              )}

            </Box>
            <Stack spacing={2} direction={{ xs: "column", sm: "row" }} alignItems="flex-start">
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={200}>Bundle Name / Description</Typography>
                <TextField fullWidth placeholder="e.g., Summer Gear Pack 2024" value={name} onChange={e => setName(e.target.value)} size="small" />
              </Stack>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={200}>Destination Product SKU</Typography>
                {/*  product name variant name then sku name  */}
                <Autocomplete
                  value={parentProductsOptions.find(p => p.id === parentProductId) || null}
                  inputValue={
                    parentProductSearchText ||
                    parentProductsOptions.find(p => p.id === parentProductId)?.name ||
                    ""
                  }
                  onChange={(e, value) => {
                    setParentProductId(value?.id || "");
                    setParentProductSearchText(value?.name || "");
                    setParentPage(1);
                  }}
                  onInputChange={(e, value, reason) => {
                    if (reason === "input") {
                      setParentProductSearchText(value);
                      setParentPage(1);
                    }
                    if (reason === "clear") {
                      setParentProductSearchText("");
                      setParentProductId("");
                      setParentPage(1);
                    }
                  }}
                  options={parentProductsOptions}
                  getOptionLabel={(option) => option.name}
                  filterOptions={(x) => x}
                  ListboxProps={{ onScroll: handleParentScroll }}

                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        {/* MAIN NAME */}
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {option.name}
                        </Typography>

                        {/* OPTIONAL SUBTEXT */}
                        {option.subname && (
                          <Typography
                            sx={{ fontSize: "12px", fontWeight: 500, color: "text.secondary", lineHeight: 1.2, }} > {option.subname}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}

                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select/Search parent product..."
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Stack>
            </Stack>
            <Divider sx={{ height: 1, bgcolor: "grey.500", mb: 2, mt: 2 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={400}>Total Components Defined :</Typography>
              <Typography fontWeight={600} color="#2563eb">{distinctCount} distinct items</Typography>
            </Box>
          </Box>

          {/* Bundle Components */}
          <Box sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 1, backgroundColor: "#fff" }}>
            <Typography variant="subtitle1" fontWeight={600}>2. Bundle Components ({totalQuantity} total items)</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Stack spacing={2} direction={{ xs: "column", sm: "row" }} alignItems="stretch">
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={200}>Source(Platform)</Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={platformName}
                    onChange={e => setPlatformName(e.target.value)}
                    displayEmpty
                    disabled={editingId !== null || visibleComponents.length > 0}
                    renderValue={selected => !selected ? <span style={{ color: "#9e9e9e", fontStyle: "normal" }}>Select a platform</span> : platforms.find(p => p.id === selected)?.name ?? ""}
                  >
                    {platforms.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Stack>

              <Stack spacing={0.5} sx={{ flex: 2 }}>
                <Typography variant="subtitle1" fontWeight={200}>Component Product</Typography>
                <Autocomplete
                  value={filteredProductsList.find(p => p.id === productId) || null}
                  inputValue={search || filteredProductsList.find(p => p.id === productId)?.name || ""}
                  onChange={(e, value) => {
                    setProductId(value?.id || "");
                    setSearch(value?.name || "");
                    setProductPage(1);
                  }}
                  onInputChange={(e, value, reason) => {
                    if (reason === "input") {
                      setSearch(value);
                      setProductPage(1);
                    }
                    if (reason === "clear") {
                      setSearch("");
                      setProductId("");
                      setProductPage(1);
                    }
                  }}
                  options={filteredProductsList}
                  getOptionLabel={option => option.name}
                  filterOptions={x => x}
                  ListboxProps={{ onScroll: handleProductScroll }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        {/* MAIN NAME */}
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {option.name}
                        </Typography>

                        {/* OPTIONAL SUBTEXT */}
                        {option.sku && (
                          <Typography
                            sx={{ fontSize: "12px", fontWeight: 500, color: "text.secondary", lineHeight: 1.2, }} > {option.sku}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  renderInput={params => <TextField {...params} placeholder="Select/Search product..." size="small" fullWidth />}

                />

              </Stack>

              <Stack spacing={0.5} sx={{ width: 120 }}>
                <Typography variant="subtitle1" fontWeight={200}>Quantity</Typography>
                <TextField type="number" inputProps={{ min: 1 }} value={quantity === 0 ? "" : quantity}
                  onChange={e => {
                    const val = e.target.value;
                    const num = Number(val);
                    if (val === "") {
                      setQuantity(0); // temporarily 0 while user clears
                    } else if (!isNaN(num) && num >= 0) {
                      setQuantity(num);
                    }
                  }} size="small" fullWidth />
              </Stack>
              <Stack spacing={0.5} sx={{ width: 100 }}>
                <Typography variant="subtitle1" fontWeight={200}>Unit Price</Typography>

                <TextField
                  type="number"
                  inputProps={{ min: 0, step: "1" }}
                  value={unitPrice === null ? "" : unitPrice}   //  empty box when null
                  onChange={(e) => {
                    const val = e.target.value;

                    // empty => null
                    if (val === "") {
                      setUnitPrice(null);
                      return;
                    }

                    const num = Number(val);
                    if (!Number.isNaN(num)) {
                      setUnitPrice(num);
                    }
                  }}
                  size="small"
                  fullWidth
                />
              </Stack>

              {isWrite && (<Button variant="contained" onClick={handleAddOrUpdate} sx={{ px: 4, flexShrink: 0, alignSelf: "flex-end", backgroundColor: "#2563eb", "&:hover": { backgroundColor: "#1e4bb8" } }}>
                {editingId ? "Update" : "Add"}
              </Button>)}
            </Stack>

            <Divider sx={{ my: 2 }} />

            {visibleComponents.length > 0 && (
              <Typography sx={{ color: "red", fontSize: "13px", mt: 1 }}>
                All components must belong to the same Source Platform. Remove current items to change platform.
              </Typography>
            )}

            <Box className="border border-2 rounded p-0 mt-3">
              <DataTable
                columns={columns}
                data={visibleComponents}
                highlightOnHover
                responsive
                noDataComponent={<Box sx={{ py: 4, textAlign: "center", color: "#6b7280" }}>No Data Available</Box>}
                customStyles={{
                  table: { style: { minHeight: "35vh" } },
                  headRow: { style: { backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb" } },
                  rows: { style: { minHeight: "48px", borderBottom: "1px solid #f3f4f6" } },
                  headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <CommonAlertModal
        title="Delete Component"
        subtitle="Are you sure you want to delete this component from bundle?"
        isVisible={showDeleteModal}
        onCancel={cancelDelete}
        onSuccess={confirmDelete}
        cancelBtnTitle="No"
        successBtnTitle="Yes, Delete"
        successBtnClasses="btn btn-sm btn-danger m-2"
        showSaveButton={true}
        showCloseButton={true}
        isTermsVisible={false}
        modalClassName="bundle-editor-modal"
      />
    </>
  );
}

<style>{`
 .bundle-editor-modal {
 z-index:1500 !important;
}
`}</style>
